import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddDocumentNumbering.css";
import { API_BASE } from "../../../ApiUrl";
import SettingsTabs from "../Settings/SettingsTabs";

const API_URL = `${API_BASE}/tenant-numbering-configs/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const DOCUMENT_TYPE_SUGGESTIONS = [
  "PROJECT",
  "REPORT",
  "INSPECTION",
  "QUOTATION",
  "INVOICE",
  "PURCHASE_ORDER",
  "WORK_ORDER",
  "CERTIFICATE",
  "TASK",
  "OTHER"
];

const PATTERN_TOKENS = [
  { token: "{YYYY}", description: "4-digit year (e.g. 2025)" },
  { token: "{YY}", description: "2-digit year (e.g. 25)" },
  { token: "{MM}", description: "2-digit month (01–12)" },
  { token: "{DD}", description: "2-digit day (01–31)" },
  { token: "{SEQ}", description: "Sequence number (padded)" },
  { token: "{SEQ:4}", description: "Sequence padded to 4 digits (e.g. 0001)" }
];

const emptyNumbering = {
  id: null,
  document_type: "",
  pattern: ""
};

const AddDocumentNumbering = () => {
  const navigate = useNavigate();

  const [numberings, setNumberings] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([{ ...emptyNumbering }]);
  const [tenantId, setTenantId] = useState(null);

  // ----------------------------------------------------------
  // GET TENANT ID
  // ----------------------------------------------------------

  useEffect(() => {
    const stored = localStorage.getItem("tenant_user");
    if (!stored) {
      Swal.fire({
        icon: "error",
        title: "Tenant information missing",
        text: "Please login again."
      });
      return;
    }

    try {
      const tenantUser = JSON.parse(stored);
      if (!tenantUser.tenant_id) {
        Swal.fire({
          icon: "error",
          title: "Tenant ID missing",
          text: "Tenant information could not be found."
        });
        return;
      }
      setTenantId(Number(tenantUser.tenant_id));
    } catch (error) {
      console.error("Invalid tenant_user:", error);
      Swal.fire({
        icon: "error",
        title: "Invalid tenant session",
        text: "Please login again."
      });
    }
  }, []);

  // ----------------------------------------------------------
  // FETCH NUMBERINGS
  // ----------------------------------------------------------

  useEffect(() => {
    if (tenantId) fetchNumberings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchNumberings = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["tenant_numbering_configs"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.tenant_numbering_configs) {
        rows = result.data.tenant_numbering_configs;
      } else if (result?.tenant_numbering_configs) {
        rows = result.tenant_numbering_configs;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setNumberings(rows);
    } catch (error) {
      console.error("Error fetching document numberings:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load numberings",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading document numberings."
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // FORM HANDLERS
  // ----------------------------------------------------------

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((n, i) => (i === index ? { ...n, [name]: value } : n))
    );
  };

  const handleAdd = () => {
    setFormData([{ ...emptyNumbering }]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, { ...emptyNumbering }]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (n) => {
    setFormData([
      {
        id: n.id,
        document_type: n.document_type || "",
        pattern: n.pattern || ""
      }
    ]);
    setEditingId(n.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (n) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Numbering?",
      text: `Are you sure you want to delete "${
        n.document_type || "this numbering"
      }"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${n.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Numbering deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchNumberings();
    } catch (error) {
      console.error("Error deleting numbering:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the numbering."
      });
    }
  };

  // ----------------------------------------------------------
  // VALIDATION
  // ----------------------------------------------------------

  const validateRow = (n, index) => {
    const label = `Numbering ${index + 1}`;

    const isEmptyRow = !n.document_type.trim() && !n.pattern.trim() && !n.id;
    if (isEmptyRow) return true;

    if (!n.document_type.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Document Type Required",
        text: `Please provide a Document Type for ${label}.`
      });
      return false;
    }

    if (n.document_type.trim().length > 30) {
      Swal.fire({
        icon: "warning",
        title: "Document Type Too Long",
        text: `Document Type for ${label} must be 30 characters or fewer.`
      });
      return false;
    }

    if (!n.pattern.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Pattern Required",
        text: `Please provide a Pattern for ${label}.`
      });
      return false;
    }

    if (n.pattern.trim().length > 100) {
      Swal.fire({
        icon: "warning",
        title: "Pattern Too Long",
        text: `Pattern for ${label} must be 100 characters or fewer.`
      });
      return false;
    }

    if (!/\{SEQ(?::\d+)?\}/i.test(n.pattern)) {
      Swal.fire({
        icon: "warning",
        title: "Missing Sequence Token",
        text: `Pattern for ${label} must contain a {SEQ} or {SEQ:n} token.`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (n) => ({
    document_type: n.document_type.trim().toUpperCase(),
    pattern: n.pattern.trim(),
    tenant: Number(tenantId)
  });

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  // ----------------------------------------------------------
  // SUBMIT
  // ----------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant."
      });
      return;
    }

    for (let i = 0; i < formData.length; i++) {
      if (!validateRow(formData[i], i)) return;
    }

    const activeRows = formData.filter(
      (n) => n.document_type.trim() || n.pattern.trim() || n.id
    );

    const types = activeRows.map((n) =>
      n.document_type.trim().toUpperCase()
    );
    const duplicateIndex = types.findIndex((t, i) => types.indexOf(t) !== i);

    if (duplicateIndex !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Document Type",
        text: `Document Type "${activeRows[
          duplicateIndex
        ].document_type.trim()}" is used more than once.`
      });
      return;
    }

    try {
      setLoading(true);

      if (isEditing) {
        const payload = buildPayload(formData[0]);
        await axios.put(`${API_URL}${editingId}/`, payload, {
          headers: { "Content-Type": "application/json" }
        });

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const n of activeRows) {
          await axios.post(API_URL, buildPayload(n), {
            headers: { "Content-Type": "application/json" }
          });
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} numbering${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyNumbering }]);

      await fetchNumberings();
    } catch (error) {
      console.error("Error saving numbering:", error);

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyNumbering }]);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="doc-num-page">
          <SettingsTabs />

          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="doc-num-page-header">
                <div>
                  <h2>Document Numbering</h2>
                </div>

                <button
                  type="button"
                  className="doc-num-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add Numbering
                </button>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="doc-num-list-card">
                {loading ? (
                  <div className="doc-num-loading">
                    Loading numberings...
                  </div>
                ) : numberings.length === 0 ? (
                  <div className="doc-num-empty">
                    <div className="doc-num-empty-icon">+</div>
                    <h3>No Numbering Patterns Found</h3>
                    <p>
                      There are no document numbering patterns available for
                      the current tenant.
                    </p>
                    <button
                      type="button"
                      className="doc-num-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Numbering
                    </button>
                  </div>
                ) : (
                  <div className="doc-num-table-wrapper">
                    <table className="doc-num-table">
                      <thead>
                        <tr>
                          <th>Document Type</th>
                          <th>Pattern</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {numberings.map((n) => (
                          <tr key={n.id}>
                            <td>
                              <span className="doc-num-type-pill">
                                {n.document_type || "-"}
                              </span>
                            </td>

                            <td>
                              <code className="doc-num-pattern-code">
                                {n.pattern || "-"}
                              </code>
                            </td>

                            <td>
                              <div className="doc-num-actions">
                                <button
                                  type="button"
                                  className="doc-num-edit-btn"
                                  onClick={() => handleEdit(n)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="doc-num-delete-btn"
                                  onClick={() => handleDelete(n)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="doc-num-page-header">
                <div>
                  <h2>
                    {isEditing
                      ? "Edit Document Numbering"
                      : "Create Document Numberings"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="doc-num-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  FORM
              ================================================== */}

              <form onSubmit={handleSubmit}>
                {formData.map((n, index) => (
                  <div
                    className="doc-num-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="doc-num-form-body">
                      <div className="doc-num-record-header">
                        <h4>Numbering {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="doc-num-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="doc-num-form-grid">
                        <div className="doc-num-form-group">
                          <label>
                            Document Type <span>*</span>
                          </label>
                          <input
                            type="text"
                            name="document_type"
                            value={n.document_type}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. PROJECT"
                            list="doc-type-suggestions"
                            maxLength={30}
                            required
                          />
                          <datalist id="doc-type-suggestions">
                            {DOCUMENT_TYPE_SUGGESTIONS.map((v) => (
                              <option key={v} value={v} />
                            ))}
                          </datalist>
                        </div>

                        <div className="doc-num-form-group doc-num-form-group--full">
                          <label>
                            Pattern <span>*</span>
                          </label>
                          <input
                            type="text"
                            name="pattern"
                            value={n.pattern}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. PRJ-{YYYY}-{SEQ:4}"
                            maxLength={100}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="doc-num-tokens">
                  <strong>Pattern tokens</strong>
                  <div className="doc-num-tokens-grid">
                    {PATTERN_TOKENS.map((t) => (
                      <div className="doc-num-token" key={t.token}>
                        <code>{t.token}</code>
                        <span>{t.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {!isEditing && (
                  <button
                    type="button"
                    className="doc-num-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Numbering
                  </button>
                )}

                <div className="doc-num-form-actions">
                  <button
                    type="button"
                    className="doc-num-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="doc-num-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Numbering"
                      : "Save Numberings"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddDocumentNumbering;